import { Button, Box, Tab, Tooltip, Typography } from '@mui/material';
import { GlobeIcon } from '@tix-ticketing/ui-icons/outline';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { IconButton } from '../components/button/icon-button';
import { TabBar } from '../components/tab-bar';
import { TixSelector } from '../components/tix-selector';
import { useIsMobile } from '../hooks/use-is-mobile';
export interface LocaleSelectorOption {
  value: string;
  title: ReactNode;
  subtitle?: ReactNode;
}
export interface LocaleSelectorTab {
  label: ReactNode;
  options: LocaleSelectorOption[];
  selected?: string;
  onSelect(value: string): void;
}
interface LocaleSelectorProps {
  /** Tooltip and accessible name of the trigger. */
  label: string;
  /** Desktop trigger text, e.g. the current language. */
  triggerLabel: ReactNode;
  /** Secondary desktop trigger text, e.g. the current currency. */
  triggerMeta?: ReactNode;
  tabs: LocaleSelectorTab[];
}
function OptionButton({
  selected,
  title,
  subtitle,
  onClick
}: {
  selected: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  onClick(): void;
}) {
  return <Button variant="outlined" size="medium" color="monochrome" onClick={onClick} sx={theme => {
    const borderWidth = selected ? 2 : 1;
    const border = `${borderWidth}px solid ${selected ? theme.palette.primary.main : theme.palette.monochrome.outlineRestingBorder}`;
    return {
      border,
      '&:hover': {
        border
      },
      // An option tile, so it takes the form item radius; `!important`
      // beats the button's own breakpoint radius.
      borderRadius: `${theme.formItemRadius.main}px !important`,
      width: '100%',
      px: 2,
      py: 1.25,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      textAlign: 'left',
      textTransform: 'none'
    };
  }}>
      <Typography variant="body2Medium" color="text.primary">
        {title}
      </Typography>
      {subtitle && <Typography variant="body3" color="text.secondary">
          {subtitle}
        </Typography>}
    </Button>;
}

// Globe button opening tabs of option buttons: a bottom sheet on mobile, a
// popper on desktop.
export function LocaleSelector({
  label,
  triggerLabel,
  triggerMeta,
  tabs
}: LocaleSelectorProps) {
  const isMobile = useIsMobile();
  const popupId = `locale-selector-${useId()}`;
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const popupState = {
    popupId,
    isOpen,
    anchorEl,
    close: () => setIsOpen(false)
  };
  const toggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(open => !open);
  };
  const button = isMobile ? <IconButton color="monochrome" size="medium" label={label}
  // The tooltip would otherwise sit on top of the open selector panel.
  hideTooltip={isOpen} sx={theme => ({
    color: 'monochrome.contrastText',
    background: isOpen ? theme.palette.action.hover : undefined
  })} onClick={toggle}>
      <GlobeIcon />
    </IconButton> :
  // The tooltip would otherwise sit on top of the open selector panel.
  // The title is an element rather than a string so MUI does not turn it
  // into an aria-label that shadows the button's own text.
  <Tooltip title={isOpen ? '' : <span>{label}</span>} placement="bottom">
      <Button startIcon={<GlobeIcon />} color="monochrome" aria-haspopup="true" aria-controls={isOpen ? popupId : undefined} aria-expanded={isOpen} onClick={toggle} sx={theme => ({
      background: isOpen ? theme.palette.action.hover : undefined
    })}>
        <Typography variant="body2Medium">{triggerLabel}</Typography>
        {triggerMeta && <Typography variant="body2" sx={{
        ml: 1,
        mt: '2px'
      }}>
            ({triggerMeta})
          </Typography>}
      </Button>
    </Tooltip>;
  return <TixSelector triggerElement={button} open={isOpen} onClose={popupState.close} localeState={popupState} popperPlacement="bottom-end">
      <Box sx={{
      mt: -2
    }}>
        <Box sx={{
        borderBottom: 1,
        borderColor: 'divider'
      }}>
          <TabBar size="small" value={tab} onChange={(_, value: number) => setTab(value)}>
            {tabs.map((t, index) => <Tab key={index} id={`${popupId}-tab-${index}`} aria-controls={`${popupId}-tabpanel-${index}`} label={t.label} />)}
          </TabBar>
        </Box>
        {tabs.map((t, index) => <Box key={index} id={`${popupId}-tabpanel-${index}`} role="tabpanel" aria-labelledby={`${popupId}-tab-${index}`} hidden={tab !== index} sx={{
        pt: 3
      }}>
            <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1
        }}>
              {t.options.map((option, i) => <OptionButton key={`${option.value}-${i}`} selected={t.selected === option.value} title={option.title} subtitle={option.subtitle} onClick={() => {
            t.onSelect(option.value);
            popupState.close();
          }} />)}
            </Box>
          </Box>)}
      </Box>
    </TixSelector>;
}